import { useDisplayName } from '@/composables/useDisplayName';
import { getUserSalutationName } from 'common/utils/name.utils';

export interface IMailRecipient {
  email: string;
}

interface IBuildRequestClassMailtoArgs<T extends IMailRecipient> {
  recipients: readonly (T & { firstName?: string | null })[];
  sender:
    | {
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined;
  assignmentPath: string;
  subject: string;
  body: (params: {
    firstNameReceiver: string;
    firstNameSender: string;
    fullNameSender: string;
    classAssignmentLink: string;
  }) => string;
}

interface IResolvePrimaryRecipientWithCcResult<T extends IMailRecipient> {
  receiver: T | null;
  ccRecipients: string[];
}

interface IBuildMailtoLinkArgs {
  to: string;
  ccRecipients?: string[];
  subject: string;
  body: string;
}

export function resolvePrimaryRecipientWithCc<T extends IMailRecipient>(
  recipients: readonly T[],
): IResolvePrimaryRecipientWithCcResult<T> {
  const receiver = recipients.at(0) ?? null;
  const receiverEmail = receiver?.email;

  const usedEmails = new Set<string>();
  if (receiverEmail) usedEmails.add(receiverEmail);

  const ccRecipients: string[] = [];
  for (const recipient of recipients.slice(1)) {
    if (usedEmails.has(recipient.email)) continue;
    usedEmails.add(recipient.email);
    ccRecipients.push(recipient.email);
  }

  return {
    receiver,
    ccRecipients,
  };
}

export function buildMailtoLink({ to, ccRecipients = [], subject, body }: IBuildMailtoLinkArgs): string {
  let mailto = `mailto:${to}?`;
  if (ccRecipients.length > 0) {
    mailto += `cc=${ccRecipients.join(',')}&`;
  }
  mailto += `subject=${encodeURIComponent(subject)}`;
  mailto += `&body=${encodeURIComponent(body)}`;
  return mailto;
}

export function buildRequestClassMailto<T extends IMailRecipient>({
  recipients,
  sender,
  assignmentPath,
  subject,
  body,
}: IBuildRequestClassMailtoArgs<T>): string | null {
  const { receiver, ccRecipients } = resolvePrimaryRecipientWithCc(recipients);
  if (!receiver) return null;

  const { getUserDisplayName } = useDisplayName();
  const firstNameSender = getUserSalutationName(sender) || sender?.email || '';
  const fullNameSender = getUserDisplayName(sender ?? {}) || sender?.email || '';
  const classAssignmentLink = assignmentPath.startsWith('/')
    ? `${globalThis.location.origin}${assignmentPath}`
    : assignmentPath;

  return buildMailtoLink({
    to: receiver.email,
    ccRecipients,
    subject,
    body: body({
      firstNameReceiver: getUserSalutationName(receiver) || receiver.email,
      firstNameSender,
      fullNameSender,
      classAssignmentLink,
    }),
  });
}
